import React, { useEffect, useRef, useState } from 'react';
import * as firebaseui from 'firebaseui';
import firebase from 'firebase/compat/app';
import { Modal, Spin } from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import { useLoan } from '../../../stores/store';

import 'firebaseui/dist/firebaseui.css';

type Props = {
  visible: boolean;
  mode: 'forced' | 'standard';
  email?: string;
  name?: string;
  text?: string;
  onLoginSuccess: () => void;
  onCancel: () => void;
};

const DEFAULT_TEXT =
  'Login or create an account to save your application or continue where you left off.';

export default function LendAPILoginModal({
  visible,
  mode,
  email,
  name,
  text,
  onLoginSuccess,
  onCancel,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const auth = useAuth();
  const loanStore = useLoan();
  const observerRef = useRef<MutationObserver | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nameObserverRef = useRef<MutationObserver | null>(null);
  const namePollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const uiStartedRef = useRef(false);

  const isForced = mode === 'forced';

  const fillEmailInput = (container: HTMLElement, emailValue: string): void => {
    const emailInput = container.querySelector<HTMLInputElement>(
      'input[type="email"], input[name="email"]'
    );
    if (emailInput && !emailInput.disabled) {
      emailInput.value = emailValue;
      emailInput.setAttribute('readonly', 'true');
      emailInput.disabled = true;
      emailInput.dispatchEvent(new Event('input', { bubbles: true }));
      console.log('[LendAPILoginModal] Email prefilled successfully');
    }
  };

  const fillNameInput = (container: HTMLElement, nameValue: string): void => {
    const nameInput = container.querySelector<HTMLInputElement>('input[name="name"]');
    if (nameInput && !nameInput.disabled) {
      nameInput.value = nameValue;
      nameInput.disabled = true;
      nameInput.dispatchEvent(new Event('input', { bubbles: true }));
      console.log('[LendAPILoginModal] Name prefilled successfully');
    }
  };

  const cleanupEmailPreFill = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const cleanupNamePreFill = () => {
    if (nameObserverRef.current) {
      nameObserverRef.current.disconnect();
      nameObserverRef.current = null;
    }
    if (namePollIntervalRef.current) {
      clearInterval(namePollIntervalRef.current);
      namePollIntervalRef.current = null;
    }
  };

  const setupEmailPreFill = (emailValue: string) => {
    const container = document.getElementById('firebase-auth-ui-combined');
    if (!container) return;

    observerRef.current = new MutationObserver(() => {
      fillEmailInput(container, emailValue);
    });

    observerRef.current.observe(container, { childList: true, subtree: true });

    pollIntervalRef.current = setInterval(() => {
      fillEmailInput(container, emailValue);
    }, 500);
  };

  const setupNamePreFill = (nameValue: string) => {
    const container = document.getElementById('firebase-auth-ui-combined');
    if (!container) return;

    nameObserverRef.current = new MutationObserver(() => {
      fillNameInput(container, nameValue);
    });

    nameObserverRef.current.observe(container, { childList: true, subtree: true });

    namePollIntervalRef.current = setInterval(() => {
      fillNameInput(container, nameValue);
    }, 500);
  };

  useEffect(() => {
    if (!visible) {
      uiStartedRef.current = false;
      return;
    }

    const startFirebaseUI = () => {
      if (uiStartedRef.current) return;

      let ui = firebaseui.auth.AuthUI.getInstance();
      if (!ui) {
        ui = new firebaseui.auth.AuthUI(auth);
      }

      uiStartedRef.current = true;

      ui.start('#firebase-auth-ui-combined', {
        callbacks: {
          signInFailure(error: any) {
            setLoading(true);
            loanStore.setIsUpgradingUser(true);
            const anonUser = auth.currentUser;
            if (!anonUser) {
              setErrorMessage('Missing user! Please try again or refresh the page.');
              setLoading(false);
              loanStore.setIsUpgradingUser(false);
              return Promise.resolve();
            }
            if (error.code !== 'firebaseui/anonymous-upgrade-merge-conflict') {
              setErrorMessage(
                'Sorry! An unknown error happened. Please try again or refresh the page.'
              );
              setLoading(false);
              loanStore.setIsUpgradingUser(false);
              return Promise.resolve();
            }
            const cred = error.credential;
            const functions = firebase.functions();
            const upgradeUser = functions.httpsCallable('upgradeUser');
            const payload = {
              token: cred.idToken || JSON.stringify({ email: cred._email }),
            };

            return upgradeUser(payload)
              .then(() => firebase.auth().signInWithCredential(cred))
              .then(() => {
                setLoading(false);
                loanStore.setIsUpgradingUser(false);
                onLoginSuccess();
              })
              .catch((err: any) => {
                console.error('[LendAPILoginModal] Error upgrading user:', err);
                console.error(
                  '[Sentry] [LendAPILoginModal] FirebaseUI widget will not auto-restart. User must refresh the page to retry sign-in.'
                );
                setErrorMessage(
                  'Sorry! An error occurred during sign-in. Please refresh the page to try again.'
                );
                setLoading(false);
                loanStore.setIsUpgradingUser(false);
              });
          },
          signInSuccessWithAuthResult() {
            onLoginSuccess();
            return false;
          },
          uiShown() {
            setLoading(false);
          },
        },
        autoUpgradeAnonymousUsers: true,
        signInFlow: 'popup',
        signInOptions: [
          {
            provider: firebase.auth.GoogleAuthProvider.PROVIDER_ID,
            scopes: ['https://www.googleapis.com/auth/userinfo.email'],
          },
          firebase.auth.EmailAuthProvider.PROVIDER_ID,
        ],
      });

      if (email) {
        setupEmailPreFill(email);
      }
      if (name) {
        setupNamePreFill(name);
      }
    };

    const timer = setTimeout(startFirebaseUI, 100);
    return () => {
      clearTimeout(timer);
      cleanupEmailPreFill();
      cleanupNamePreFill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <Modal
      visible={visible}
      footer={null}
      closable={!isForced}
      maskClosable={!isForced}
      keyboard={!isForced}
      onCancel={isForced ? undefined : onCancel}
      centered
      destroyOnClose
    >
      <div>
        <h3>
          <b>Sign In</b>
        </h3>
        <h4>{text || DEFAULT_TEXT}</h4>
        {errorMessage && <p>{errorMessage}</p>}
        {loading && <Spin size="large" style={{ marginTop: '2rem', display: 'block' }} />}
        <div id="firebase-auth-ui-combined" style={loading ? { display: 'none' } : undefined} />
      </div>
    </Modal>
  );
}
